// src/pages/ReactorDetail.tsx
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Grid, Box, Alert, CircularProgress, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import {AccessTime, ArrowBack} from '@mui/icons-material';

interface ReactorData {
  job_id: string;
  meta: {
    account_names: string[];
    channels: string[];
    reactions: string[];
    min_delay: number;
    max_delay: number;
    spread_reactions: boolean;
    max_spread_hours: number;
    started_at: string;
  };
  stats: {
    total_messages_seen: number;
    total_views_added: number;
    total_reactions_added: number;
    total_errors: number;
    channels_stats: Record<string, {
      messages_seen: number;
      views_added: number;
      reactions_added: number;
      errors: number;
      last_activity_at?: string;
    }>;
    accounts_stats: Record<string, {
      views_added: number;
      reactions_added: number;
      errors: number;
      last_reaction_at?: string;
    }>;
    last_reaction_at?: string;
    last_error?: { message: string; timestamp: string } | null;
    last_activity_at?: string;
    started_at: string;
  };
  process: {
    status: string;
    stopped_at?: string | null;
    died_at?: string | null;
  };
}

const ReactorDetail: React.FC = () => {
  const { jobId } = useParams<{ jobId?: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<ReactorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Защита от undefined jobId
  useEffect(() => {
    if (!jobId) {
      navigate('/reactor');
    }
  }, [jobId, navigate]);

  const fetchStatus = async () => {
    if (!jobId) return;

    try {
      const res = await api.get(`/reactors/status/${jobId}`);
      setData(res.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Задача не найдена');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchStatus();
      const interval = setInterval(fetchStatus, 8000);
      return () => clearInterval(interval);
    }
  }, [jobId]);

  if (!jobId) return null;

  if (loading) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <CircularProgress size={70} />
        <Typography variant="h6" mt={3}>Загрузка статистики реактора...</Typography>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Задача не найдена или была удалена'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/reactor')}>
          Назад к реакторам
        </Button>
      </Container>
    );
  }

  const { meta } = data;
 const stats = data.stats ?? {
  total_messages_seen: 0,
  total_views_added: 0,
  total_reactions_added: 0,
  total_errors: 0,
  channels_stats: {},
  accounts_stats: {},
  started_at: meta.started_at,
  last_reaction_at: null,
  last_error: null,
  last_activity_at: null,
};

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/reactor')} sx={{ mb: 4 }}>
        Назад к реакторам
      </Button>

      <Paper elevation={4} sx={{ p: 5, borderRadius: 3, bgcolor: '#F8FAFC' }}>
        <Typography variant="h4" fontWeight="bold" color="#1E3A8A" gutterBottom>
          Реактор #{jobId.slice(0, 8)}
        </Typography>

        <Typography color="#4B5563" fontSize="1.1rem" mb={4}>
          {meta.channels.length === 1
            ? <>Канал: <strong>{meta.channels[0]}</strong></>
            : <>Каналы: <strong>{meta.channels.join(', ')}</strong></>
          } · Аккаунтов: <strong>{meta.account_names.length}</strong> ·
          <AccessTime sx={{ verticalAlign: 'middle', mx: 1 }} />
          Запущено: <strong>{new Date(meta.started_at).toLocaleString('ru-RU')}</strong>
        </Typography>

        {/* Основные счётчики */}
        <Box sx={{my: 6}}>
          <Grid container spacing={3}>

            {/* Просмотры */}
            <Grid size={{xs: 6, md: 3}}>
              <Paper sx={{p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2}}>
                <Typography variant="h3" fontWeight="bold" color="#2563EB">
                  {stats?.total_views_added ?? 0}
                </Typography>
                <Typography color="text.secondary">Просмотров</Typography>
              </Paper>
            </Grid>

            {/* Реакции */}
            <Grid size={{xs: 6, md: 3}}>
              <Paper sx={{p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2}}>
                <Typography variant="h3" fontWeight="bold" color="#10B981">
                  {stats?.total_reactions_added ?? 0}
                </Typography>
                <Typography color="text.secondary">Реакций</Typography>
              </Paper>
            </Grid>

            {/* Сообщения */}
            <Grid size={{xs: 6, md: 3}}>
              <Paper sx={{p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2}}>
                <Typography variant="h3" fontWeight="bold" color="#8B5CF6">
                  {stats?.total_messages_seen ?? 0}
                </Typography>
                <Typography color="text.secondary">Сообщений</Typography>
              </Paper>
            </Grid>

            {/* Ошибки */}
            <Grid size={{xs: 6, md: 3}}>
              <Paper sx={{p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2}}>
                <Typography
                    variant="h3"
                    fontWeight="bold"
                    color={(stats?.total_errors ?? 0) > 0 ? '#DC2626' : '#6B7280'}
                >
                  {stats?.total_errors ?? 0}
                </Typography>
                <Typography color="text.secondary">Ошибок</Typography>
              </Paper>
            </Grid>

          </Grid>
        </Box>

        {/* Настройки */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
            Настройки
          </Typography>
          <Grid container spacing={3}>
            <Grid size={6}><strong>Реакции:</strong> {meta.reactions.join(' ')}</Grid>
            <Grid size={6}><strong>Задержка:</strong> {meta.min_delay}–{meta.max_delay} сек</Grid>
            <Grid size={6}><strong>Растягивание:</strong> {meta.spread_reactions ? 'Да' : 'Нет'}</Grid>
          </Grid>
        </Box>

        {/* По каналам */}
        {Object.keys(stats.channels_stats).length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
              По каналам
            </Typography>
            <TableContainer component={Paper} elevation={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Канал</strong></TableCell>
                    <TableCell align="right"><strong>Сообщений</strong></TableCell>
                    <TableCell align="right"><strong>Просмотры</strong></TableCell>
                    <TableCell align="right"><strong>Реакций</strong></TableCell>
                    <TableCell align="right"><strong>Ошибок</strong></TableCell>
                    <TableCell><strong>Последняя активность</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(stats.channels_stats).map(([ch, s]) => (
                    <TableRow key={ch}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{ch}</TableCell>
                      <TableCell align="right" sx={{ color: '#8B5CF6' }}>{s.messages_seen}</TableCell>
                      <TableCell align="right" sx={{ color: '#2563EB' }}>{s.views_added}</TableCell>
                      <TableCell align="right" sx={{ color: '#10B981' }}>{s.reactions_added}</TableCell>
                      <TableCell align="right" sx={{ color: s.errors > 0 ? '#DC2626' : 'inherit' }}>
                        {s.errors}
                      </TableCell>
                      <TableCell>
                        {s.last_activity_at
                          ? new Date(s.last_activity_at).toLocaleTimeString('ru-RU')
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* По аккаунтам */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
            По аккаунтам
          </Typography>
          <TableContainer component={Paper} elevation={2}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Аккаунт</strong></TableCell>
                  <TableCell align="right"><strong>Просмотры</strong></TableCell>
                  <TableCell align="right"><strong>Реакций</strong></TableCell>
                  <TableCell align="right"><strong>Ошибок</strong></TableCell>
                  <TableCell><strong>Последняя</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(stats.accounts_stats).map(([name, s]) => (
                  <TableRow key={name}>
                    <TableCell>{name}</TableCell>
                    <TableCell align="right" sx={{color: '#2563EB'}}>
                      {s.views_added ?? 0}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#10B981' }}>{s.reactions_added}</TableCell>
                    <TableCell align="right" sx={{ color: s.errors > 0 ? '#DC2626' : 'inherit' }}>
                      {s.errors}
                    </TableCell>
                    <TableCell>
                      {s.last_reaction_at ? new Date(s.last_reaction_at).toLocaleTimeString('ru-RU') : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Ошибка */}
        {stats.last_error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            <strong>Последняя ошибка:</strong> {stats.last_error.message}
            <br />
            <Typography component="span" fontSize="0.85rem">
              {new Date(stats.last_error.timestamp).toLocaleString('ru-RU')}
            </Typography>
          </Alert>
        )}

        {/* Информация снизу */}
        <Grid container spacing={3} sx={{ mt: 4, color: '#4B5563' }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <strong>Последняя активность:</strong>{' '}
            {stats.last_activity_at
              ? new Date(stats.last_activity_at).toLocaleString('ru-RU')
              : '—'}
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default ReactorDetail;